import { readonly, ref, shallowRef, watch, watchEffect, type Ref } from "vue";
import useWooCart from "./useWooCart";
import {apiFetch} from "@/libs/wordpress.facade";
import type { WoocommerceCheckoutRequest, WoocommerceCheckoutResponse, WoocommerceCheckoutUpdateRequest } from "@/types/woocommerce.types";

const checkout = shallowRef<WoocommerceCheckoutResponse|undefined>();
const selectedPaymentMethodId = ref<string>();

export function useCheckoutCustomField(key: string, type: 'checkbox', initialValue: boolean|string){
	const value = ref(initialValue);
	const enabled = ref(false);
	const label = ref('');
	const required = ref(false);
	const hasError = ref(false);

	watchEffect(() => {
		if(checkout.value?.extensions.itlp.custom_fields[key]){
			label.value = checkout.value?.extensions.itlp.custom_fields[key].label;
			required.value = Boolean(checkout.value?.extensions.itlp.custom_fields[key].required);
			enabled.value = true;
		}else{
			enabled.value = false;
		}
		hasError.value = postOrderBodyError.value?.data?.details?.additional_fields?.data?.key == key;
	});

	watch(value, () => {
		if(hasError.value) hasError.value = false;
	});
	
	function validate(){
		if(!enabled.value || !required.value) return true;
		if(!Boolean(value.value)){
			hasError.value = true;
		}
		return Boolean(value.value);
	}

	return {key, value, label, type, enabled, required, hasError, validate};
}

const wooCart = useWooCart();

function useWooCheckoutEndpoint<T>(method?: 'GET'|'POST'|'PUT'): [(data?: T) => Promise<unknown>, Ref, Ref]{
	let abortController: AbortController;

	const error = shallowRef<unknown>();
	const errorBodyResponse = shallowRef<object|undefined>();

	async function call(data?: T){
		error.value = undefined;
		errorBodyResponse.value = undefined;
		if(abortController) abortController.abort();
		abortController = new AbortController()
		const abortSignal = abortController.signal;

		if(wooCart.requesting.value != 'paying') wooCart.requesting.value = 'checkout';
		try{
			const res = (await apiFetch({
				path: '/wc/store/v1/checkout?_locale=site',
				method,
				data,
				parse: false,
				cache: 'no-store',
				signal: abortSignal
			}) as Response);
			checkout.value = await res.json();

			selectedPaymentMethodId.value = checkout.value?.payment_method;
		}catch(e: any){
			if(abortSignal.aborted) return;
			error.value = e; // e is Response when request fail (not http success)
			if(e && e instanceof Response) errorBodyResponse.value = await e.json();
		}
		if(wooCart.requesting.value == 'checkout') wooCart.requesting.value = false;

		return error.value;
	};

	return [call, error, errorBodyResponse];
}

const [refreshCheckout, refreshCheckoutError] = useWooCheckoutEndpoint();
const [placeOrder, postOrderError, postOrderBodyError] = useWooCheckoutEndpoint<WoocommerceCheckoutRequest>('POST');
const [updateCheckout, updateCheckoutError] = useWooCheckoutEndpoint<WoocommerceCheckoutUpdateRequest>('PUT');


export default function (){

	return {
		checkout: readonly(checkout),
		refreshCheckout,
		placeOrder,
		updateCheckout,
		refreshCheckoutError,
		postOrderError,
		postOrderBodyError,
		updateCheckoutError,
		selectedPaymentMethodId
	}
}